import React from 'react';

import PropTypes from 'prop-types';
import Stickyard from 'stickyard';

import { useLocale } from '@adsk/alloy-react-helpers';

import {
  defaultGroupHeaderRenderer,
  defaultSelectBoxRenderer,
  defaultMenuRenderer,
  defaultItemRenderer,
  defaultLabelRenderer,
} from '../layout/defaultRenderers';
import SelectBox from '../SelectBox';

export const GROUP_TYPE = '__selectbox_group_header__';

function flattenOptions(options, groupOptionsKey, valueKey) {
  const groupMap = {};
  const newOptions = options.reduce((flattenedOptions, group) => {
    const items = group[groupOptionsKey];
    if (Array.isArray(items)) {
      if (items.length > 0) {
        items.forEach((item) => {
          groupMap[item[valueKey]] = group;
        });
        flattenedOptions = flattenedOptions.concat(items);
      }
    } else {
      flattenedOptions.push(group);
    }
    return flattenedOptions;
  }, []);
  return [newOptions, groupMap];
}

/**
 * GroupedSelectBox, extends [SelectBox](selectbox) to add grouped support
 */
class GroupedSelectBox extends React.Component {
  constructor(props) {
    super(props);

    this.getGroupLabel = this.getGroupLabel.bind(this);
    this.getOptionLabel = this.getOptionLabel.bind(this);
    this.getOptionSubtext = this.getOptionSubtext.bind(this);
    this.menuRenderer = this.menuRenderer.bind(this);
    this.itemRenderer = this.itemRenderer.bind(this);
    this.optionRenderer = this.optionRenderer.bind(this);
    this.handleClose = this.handleClose.bind(this);

    const { options, groupOptionsKey, valueKey } = this.props;
    [this.options, this.groupMap] = flattenOptions(
      options,
      groupOptionsKey,
      valueKey
    );
    this.selectedKeys = [];
  }

  getGroupLabel(item) {
    const { groupLabelKey, groupLabelGetter } = this.props;
    return groupLabelGetter
      ? groupLabelGetter(item)
      : item[groupLabelKey] || '';
  }

  getOptionLabel(item) {
    const { labelGetter, labelKey } = this.props;
    return labelGetter ? labelGetter(item) : item[labelKey] || '';
  }

  getOptionSubtext(item) {
    const { subtextGetter, subtextKey } = this.props;
    return subtextGetter ? subtextGetter(item) : item[subtextKey] || '';
  }

  menuRenderer(args) {
    const {
      multiple,
      valueKey,
      stickyHeader,
      maxMenuHeight = 300,
      menuRenderer = defaultMenuRenderer,
    } = this.props;
    if (!this.selectedKeys.length && multiple) {
      const { selectedValue } = args;
      this.selectedKeys = Array.isArray(selectedValue)
        ? selectedValue.map((x) => x[valueKey])
        : [];
    }

    this.lastGroup = undefined;
    this.registerSticky = undefined;

    if (!stickyHeader) return menuRenderer(args);

    return (
      <Stickyard>
        {({ registerContainer, registerSticky }) => {
          this.registerSticky = registerSticky;
          return (
            <div ref={registerContainer} style={{ maxHeight: maxMenuHeight }}>
              {menuRenderer(args)}
            </div>
          );
        }}
      </Stickyard>
    );
  }

  itemRenderer(args) {
    const {
      valueKey,
      showValueAsTags,
      itemRenderer = defaultItemRenderer,
      groupHeaderRenderer = defaultGroupHeaderRenderer,
    } = this.props;
    const value = args.item[valueKey];
    const group = this.groupMap[value];

    if (
      (showValueAsTags || this.selectedKeys.indexOf(value) === -1) &&
      group &&
      group !== this.lastGroup
    ) {
      this.lastGroup = group;
      const headerProps = {
        ref: this.registerSticky,
        key: this.getGroupLabel(group),
      };
      const header = groupHeaderRenderer(group, headerProps, this.props);
      return [header, itemRenderer(args)];
    }
    return itemRenderer(args);
  }

  optionRenderer(option, selected, selectProps) {
    const { valueKey, showValueAsTags, optionRenderer } = this.props;
    const value = option[valueKey];

    if (optionRenderer) return optionRenderer(option, selected, selectProps);

    let label = this.getOptionLabel(option);
    const subtext = this.getOptionSubtext(option);
    // if selected, append group title to the option label
    if (!showValueAsTags && this.selectedKeys.indexOf(value) >= 0) {
      const group = this.groupMap[value];
      if (group) {
        const groupLabel = this.getGroupLabel(group);
        label = `${label} (${groupLabel})`;
      }
    }

    return defaultLabelRenderer(label, subtext);
  }

  handleClose() {
    this.selectedKeys = [];
    this.props.onClose && this.props.onClose();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.options !== this.props.options) {
      const { options, groupOptionsKey, valueKey } = nextProps;
      [this.options, this.groupMap] = flattenOptions(
        options,
        groupOptionsKey,
        valueKey
      );
    }
  }

  render() {
    const { children, multiple, optionRenderer } = this.props;

    return children({
      ...this.props,
      options: this.options,
      menuRenderer: this.menuRenderer,
      itemRenderer: this.itemRenderer,
      optionRenderer: multiple ? this.optionRenderer : optionRenderer,
      onClose: this.handleClose,
    });
  }
}

GroupedSelectBox.GROUP_TYPE = GROUP_TYPE;

GroupedSelectBox.defaultProps = {
  ...SelectBox.defaultProps,
  children: defaultSelectBoxRenderer,
  valueKey: 'value',
  labelKey: 'label',
  subtextKey: 'subtext',
  groupLabelKey: 'label',
  groupOptionsKey: 'options',
  stickyHeader: true,
};

GroupedSelectBox.propTypes = {
  ...SelectBox.propTypes,
  /**
   * render function
   */
  children: PropTypes.func.isRequired,
  /**
   * The key of the group label
   */
  groupLabelKey: PropTypes.string,
  /**
   * `(group) => string`, group label formatter
   */
  groupLabelGetter: PropTypes.func,
  /**
   * The key of the group options
   */
  groupOptionsKey: PropTypes.string,
  /**
   * `(group, headerProps, selectProps) => node`, custom group header renderer
   */
  groupHeaderRenderer: PropTypes.func,
  /**
   * whether the header is sticky
   */
  stickyHeader: PropTypes.bool,
};

const WrapperGroupedSelectBox = (props) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  return <GroupedSelectBox {...props} T={T} />;
};

export default WrapperGroupedSelectBox;
